import { NavigationButton } from "@components/common/NavigationLink";
import { useUserContext } from "@contexts/UserContext/index.context";
import {
  AddCircle,
  AutoDelete,
  CalendarMonth,
  CheckCircle,
  HourglassBottom,
  Pending,
  SportsScore,
  Warning,
} from "@mui/icons-material";
import type { DeliveriesCount } from "@types_def/models/delivery.types";
import { UserRole } from "@types_def/models/user.type";
import { RoutePaths } from "@utils/constants/routes";
import { useTranslation } from "react-i18next";

import {
  useGetDeliveriesCountByAdminQuery,
  useGetDeliveriesCountByClientQuery,
} from "@/libs/generated/hooks";

import LinksSubHeader from "..";
import AddNewDeals from "../addNewDeals";

function PilotSubHeader() {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const isAdmin = userState?.["custom:role"] === UserRole.ADMIN;
  const isClient = userState?.["custom:role"] === UserRole.CLIENT;

  const { data: adminCount } = useGetDeliveriesCountByAdminQuery(undefined, {
    enabled: isAdmin,
    staleTime: 1000 * 60,
    select: (d) => d.getDeliveriesCountByAdmin,
  });
  const { data: clientCount } = useGetDeliveriesCountByClientQuery(undefined, {
    enabled: isClient,
    staleTime: 1000 * 60,
    select: (d) => d.getDeliveriesCountByClient,
  });
  const deliveriesCount = adminCount ?? clientCount;

  const numberOfDeliveriesText = (deliveryCount: keyof DeliveriesCount): string => {
    if (!deliveriesCount) {
      return "";
    }

    const count = deliveriesCount[deliveryCount];
    if (!count) {
      return "";
    }

    return ` : ${count}`;
  };

  return (
    <LinksSubHeader>
      <AddNewDeals key="addNewDeals" />
      <NavigationButton
        to={
          isAdmin
            ? RoutePaths.adminPilotDeliveryTableRequested
            : RoutePaths.clientPilotDeliveryTableRequested
        }
        includes={RoutePaths.requested}
        icon={<AddCircle fontSize="inherit" />}
      >
        {t("layouts.sub-headers.deliveries.requests")} {numberOfDeliveriesText("requestedCount")}
      </NavigationButton>
      <NavigationButton
        to={
          isAdmin
            ? RoutePaths.adminPilotDeliveryTableConfirmed
            : RoutePaths.clientPilotDeliveryTableConfirmed
        }
        includes={RoutePaths.confirmed}
        icon={<CheckCircle fontSize="inherit" />}
      >
        {t("layouts.sub-headers.deliveries.confirmed")} {numberOfDeliveriesText("confirmedCount")}
      </NavigationButton>
      {isAdmin && (
        <>
          <NavigationButton
            to={RoutePaths.adminPilotDeliveryTableWaitingForDriver}
            includes={RoutePaths.waitingForDriver}
            icon={<HourglassBottom fontSize="inherit" />}
          >
            {t("layouts.sub-headers.deliveries.waiting-for-driver")}
            {numberOfDeliveriesText("waitingDriverCount")}
          </NavigationButton>
          <NavigationButton
            to={RoutePaths.adminPilotDeliveryTableSet}
            includes={RoutePaths.set}
            icon={<CheckCircle fontSize="inherit" />}
          >
            {t("layouts.sub-headers.deliveries.assigned-to-driver")}{" "}
            {numberOfDeliveriesText("setCount")}
          </NavigationButton>
          <NavigationButton
            to={RoutePaths.adminPilotDeliveryAlert}
            includes={RoutePaths.alert}
            icon={<Warning fontSize="inherit" />}
          >
            {t("layouts.sub-headers.deliveries.alert")} {numberOfDeliveriesText("alertCount")}
          </NavigationButton>
        </>
      )}
      <NavigationButton
        to={
          isAdmin
            ? RoutePaths.adminPilotDeliveryTableOngoing
            : RoutePaths.clientPilotDeliveryTableOngoing
        }
        includes={["ongoing", "pickup-driver", "pickup-client"]}
        icon={<Pending fontSize="inherit" />}
      >
        {t("layouts.sub-headers.deliveries.ongoing")}
        {numberOfDeliveriesText("onGoingCount")}
      </NavigationButton>
      <NavigationButton
        to={
          isClient
            ? RoutePaths.clientPilotDeliveryTableDelivered
            : RoutePaths.adminPilotDeliveryTableDelivered
        }
        includes={RoutePaths.completed}
        icon={<SportsScore fontSize="inherit" />}
      >
        {t("layouts.sub-headers.deliveries.delivered")} {numberOfDeliveriesText("deliveredCount")}
      </NavigationButton>
      <NavigationButton
        to={
          isAdmin
            ? RoutePaths.adminPilotDeliveryTableCancelled
            : RoutePaths.clientPilotDeliveryTableCancelled
        }
        includes={RoutePaths.cancelled}
        icon={<AutoDelete fontSize="inherit" />}
      >
        {t("layouts.sub-headers.deliveries.cancelled")} {numberOfDeliveriesText("cancelledCount")}
      </NavigationButton>
      {isClient && (
        <NavigationButton
          to={RoutePaths.clientCalendarView}
          includes={RoutePaths.calendarView}
          icon={<CalendarMonth fontSize="inherit" />}
        >
          {t("layouts.sub-headers.deliveries.calendar")}
        </NavigationButton>
      )}
    </LinksSubHeader>
  );
}

export default PilotSubHeader;
